import { useEffect, useMemo, useRef, useState } from 'react';
import type { InkDocumentV1 } from '../../types/ink';
import { StudyActivityTimerV2 } from './studyActivityTimer';
import { composeTwoDigitMultiplicationSet } from '../learningOrchestrator/setComposer';
import type { MathWorksheetProblemV2, MathWorksheetSetV2, WorksheetPageV2 } from '../learningOrchestrator/worksheetTypes';
import {
  INITIAL_STUDY_EVALUATION_STATE,
  markBatchDispatched,
  markCorrectionDispatched,
  rejectBatchSubmission,
  rejectCorrectionSubmission,
  requestBatchSubmission,
  requestCorrectionSubmission,
  resolveBatchSubmission,
  resolveCorrectionSubmission,
  type StudyEvaluationState,
} from '../learningEngineV2/studyEvaluationFlow';
import {
  buildStudyBatchSubmissionV2,
  buildStudyCorrectionSubmissionV2,
  type StudyBatchSubmissionContextV2,
} from '../learningEngineV2/submissionBuilder';
import {
  submitStudyBatchV2,
  submitStudyCorrectionV2,
  type LearningEngineV2RpcClient,
} from '../learningEngineV2/runtimeAdapter';
import { createStudyTelemetryCollector, type StudyTelemetryEventType } from './studyTelemetry';
import { MathWorksheetProblemBlock } from './MathWorksheetProblemBlock';
import { WorksheetInkCanvas } from './WorksheetInkCanvas';
import type { GraderMarkupDocumentV1 } from './markup';
import { WorksheetMarkupLayer } from './WorksheetMarkupLayer';
import { isMathAnswerReady, updateMathAnswerDraft, type MathAnswerDraftMapV2 } from './answerCapture';
import { ProblemActivityTrackerV2, type ProblemTimingSnapshotV2 } from './problemActivity';
import { cloneInkMapV2, loadStudyDraftV2, saveStudyDraftV2, type StudyDraftBindingV2, type StudyDraftPhaseV2 } from './studyDraft';
import { studyInteractionPolicyV2 } from './studyInteractionPolicy';
import './studyV2.css';

function blankInk(): InkDocumentV1 {
  return { version: 1, width: 1000, height: 1320, strokes: [] };
}

export type StudyRuntimeBindingV2 = {
  client: LearningEngineV2RpcClient;
  studySessionId: string;
  assignmentId: string;
  worksheetVersionId: string;
};

type Props = {
  worksheet?: MathWorksheetSetV2;
  runtime?: StudyRuntimeBindingV2;
  initialEvaluation?: StudyEvaluationState;
};

function flatProblems(worksheet: MathWorksheetSetV2) {
  return worksheet.pages.flatMap((page) => page.problems);
}

function questionId(problem: MathWorksheetProblemV2) {
  return problem.submissionBinding?.questionInstanceId;
}

function resolveProblemAtPoint(page: WorksheetPageV2, x: number, y: number) {
  return page.problems.find((problem) => {
    const left = problem.placement.x * 1000;
    const top = problem.placement.y * 1320;
    const right = left + problem.placement.width * 1000;
    const bottom = top + problem.placement.height * 1320;
    return x >= left && x <= right && y >= top && y <= bottom;
  })?.problemId;
}

function buildCorrectionMarkup(page: WorksheetPageV2, targets: ReadonlySet<string>): GraderMarkupDocumentV1 {
  return {
    version: 1,
    pageId: page.pageId,
    items: page.problems.flatMap((problem) => {
      const id = questionId(problem);
      if (!id || !targets.has(id)) return [];
      return [{
        markupId: `correction-${id}`,
        kind: 'correction_required' as const,
        pageId: page.pageId,
        problemId: problem.problemId,
        x: (problem.placement.x + problem.placement.width - 0.04) * 1000,
        y: (problem.placement.y + 0.045) * 1320,
        createdAt: new Date(0).toISOString(),
        note: 'TRY_AGAIN',
      }];
    }),
  };
}

function pageIndexForQuestion(worksheet: MathWorksheetSetV2, questionInstanceId: string) {
  const index = worksheet.pages.findIndex((candidate) => candidate.problems.some((problem) => questionId(problem) === questionInstanceId));
  return index >= 0 ? index : 0;
}

export function MathStudySessionV2Page({
  worksheet: suppliedWorksheet,
  runtime,
  initialEvaluation,
}: Props = {}) {
  const fallbackWorksheet = useMemo(() => composeTwoDigitMultiplicationSet({ worksheetSetId: 'RECOVERY-MATH-V2' }), []);
  const worksheet = suppliedWorksheet ?? fallbackWorksheet;
  const draftBinding = useMemo<StudyDraftBindingV2 | null>(() => runtime ? ({
    studySessionId: runtime.studySessionId,
    assignmentId: runtime.assignmentId,
    worksheetVersionId: runtime.worksheetVersionId,
  }) : null, [runtime?.studySessionId, runtime?.assignmentId, runtime?.worksheetVersionId]);
  const restoredDraft = useMemo(() => {
    if (!draftBinding || typeof window === 'undefined') return null;
    return loadStudyDraftV2(window.localStorage, draftBinding);
  }, [draftBinding]);
  const restoredPageIndex = useMemo(() => {
    const correctionTarget = initialEvaluation?.mode === 'correction' ? initialEvaluation.remainingQuestionInstanceIds[0] : undefined;
    if (correctionTarget) return pageIndexForQuestion(worksheet, correctionTarget);
    return Math.min(Math.max(0, restoredDraft?.pageIndex ?? 0), Math.max(0, worksheet.pages.length - 1));
  }, [initialEvaluation, restoredDraft?.pageIndex, worksheet]);
  const [pageIndex, setPageIndex] = useState(restoredPageIndex);
  const [inkByPage, setInkByPage] = useState<Record<string, InkDocumentV1>>(() => restoredDraft?.studentInkByPage ?? {});
  const [frozenFirstPassInkByPage, setFrozenFirstPassInkByPage] = useState<Record<string, InkDocumentV1>>(() => restoredDraft?.frozenFirstPassInkByPage ?? restoredDraft?.studentInkByPage ?? {});
  const [correctionInkByPage, setCorrectionInkByPage] = useState<Record<string, InkDocumentV1>>(() => restoredDraft?.correctionInkByPage ?? {});
  const [answersByProblemId, setAnswersByProblemId] = useState<MathAnswerDraftMapV2>(() => restoredDraft?.answersByProblemId ?? {});
  const [correctionAnswersByQuestionId, setCorrectionAnswersByQuestionId] = useState<MathAnswerDraftMapV2>(() => restoredDraft?.correctionAnswersByQuestionId ?? {});
  const [evaluation, setEvaluation] = useState<StudyEvaluationState>(initialEvaluation ?? INITIAL_STUDY_EVALUATION_STATE);
  const collector = useRef(createStudyTelemetryCollector());
  const problemActivity = useRef(new ProblemActivityTrackerV2(undefined, restoredDraft?.problemTiming));
  const correctionActivity = useRef(new ProblemActivityTrackerV2(undefined, restoredDraft?.correctionTiming));
  const sessionTimer = useRef(new StudyActivityTimerV2(undefined, restoredDraft?.sessionTimer));
  const submissionLock = useRef(false);
  const localSessionId = useRef(`local-study-${crypto.randomUUID()}`);
  const sessionId = runtime?.studySessionId ?? localSessionId.current;
  const page = worksheet.pages[pageIndex];
  const currentInk = inkByPage[page.pageId] ?? blankInk();
  const currentFrozenFirstPassInk = frozenFirstPassInkByPage[page.pageId] ?? currentInk;
  const currentCorrectionInk = correctionInkByPage[page.pageId] ?? blankInk();
  const correctionTargets = useMemo(() => new Set(evaluation.remainingQuestionInstanceIds), [evaluation.remainingQuestionInstanceIds]);
  const markup = useMemo(() => buildCorrectionMarkup(page, correctionTargets), [page, correctionTargets]);
  const allProblems = useMemo(() => flatProblems(worksheet), [worksheet]);
  const missingFirstPassAnswers = allProblems.filter((problem) => !isMathAnswerReady(answersByProblemId[problem.problemId]));
  const activeCorrectionProblems = allProblems.filter((problem) => {
    const id = questionId(problem);
    return Boolean(id && correctionTargets.has(id));
  });
  const missingCorrectionAnswers = activeCorrectionProblems.filter((problem) => {
    const id = questionId(problem);
    return !id || !isMathAnswerReady(correctionAnswersByQuestionId[id]);
  });
  const runtimeReady = Boolean(runtime && allProblems.every((problem) => problem.submissionBinding));
  const interaction = studyInteractionPolicyV2(evaluation.mode);

  function draftPhase(): StudyDraftPhaseV2 {
    if (evaluation.mode === 'completed') return 'completed';
    if (evaluation.mode === 'correction' || evaluation.mode === 'correction_submitting') return 'correction';
    return 'study';
  }

  function persistDraftNow() {
    if (!draftBinding || typeof window === 'undefined') return;
    const atMs = Date.now();
    saveStudyDraftV2(window.localStorage, {
      schemaVersion: 2,
      contractVersion: 'le-v2-study-draft.1',
      savedAt: new Date(atMs).toISOString(),
      phase: draftPhase(),
      binding: draftBinding,
      pageIndex,
      answersByProblemId,
      studentInkByPage: inkByPage,
      frozenFirstPassInkByPage,
      correctionAnswersByQuestionId,
      correctionInkByPage,
      problemTiming: problemActivity.current.exportState(atMs),
      correctionTiming: correctionActivity.current.exportState(atMs),
      sessionTimer: sessionTimer.current.exportState(),
    });
  }

  function persistFirstPassCheckpoint(snapshot: Record<string, InkDocumentV1>) {
    if (!draftBinding || typeof window === 'undefined') return;
    const atMs = Date.now();
    saveStudyDraftV2(window.localStorage, {
      schemaVersion: 2,
      contractVersion: 'le-v2-study-draft.1',
      savedAt: new Date(atMs).toISOString(),
      phase: 'study',
      binding: draftBinding,
      pageIndex,
      answersByProblemId,
      studentInkByPage: snapshot,
      frozenFirstPassInkByPage: snapshot,
      correctionAnswersByQuestionId: {},
      correctionInkByPage: {},
      problemTiming: problemActivity.current.exportState(atMs),
      correctionTiming: correctionActivity.current.exportState(atMs),
      sessionTimer: sessionTimer.current.exportState(),
    });
  }

  useEffect(() => {
    if (!draftBinding) return;
    const timer = window.setTimeout(persistDraftNow, 120);
    return () => window.clearTimeout(timer);
    // Draft persistence intentionally follows every student-owned state mutation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftBinding, pageIndex, answersByProblemId, inkByPage, frozenFirstPassInkByPage, correctionAnswersByQuestionId, correctionInkByPage, evaluation.mode]);

  useEffect(() => {
    if (!draftBinding) return;
    const onVisibility = () => {
      problemActivity.current.pause();
      correctionActivity.current.pause();
      if (document.visibilityState === 'hidden') {
        sessionTimer.current.pause();
        persistDraftNow();
      } else {
        sessionTimer.current.resume();
      }
    };
    const onPageHide = () => {
      problemActivity.current.pause();
      correctionActivity.current.pause();
      sessionTimer.current.pause();
      persistDraftNow();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
    };
    // Event handlers always persist the latest render state through effect replacement.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftBinding, pageIndex, answersByProblemId, inkByPage, frozenFirstPassInkByPage, correctionAnswersByQuestionId, correctionInkByPage, evaluation.mode]);

  useEffect(() => {
    collector.current.record({ sessionId, type: 'SESSION_STARTED', atMs: performance.now(), pageId: page.pageId });
    collector.current.record({ sessionId, type: 'PAGE_OPENED', atMs: performance.now(), pageId: page.pageId });
    // Session identity changes only when a different authoritative runtime is mounted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  function recordTelemetry(types: StudyTelemetryEventType[]) {
    types.forEach((type) => collector.current.record({ sessionId, type, atMs: performance.now(), pageId: page.pageId }));
  }

  function changePage(nextIndex: number) {
    problemActivity.current.pause();
    correctionActivity.current.pause();
    setPageIndex(nextIndex);
    const next = worksheet.pages[nextIndex];
    collector.current.record({ sessionId, type: 'PAGE_CHANGED', atMs: performance.now(), pageId: next.pageId });
    collector.current.record({ sessionId, type: 'PAGE_OPENED', atMs: performance.now(), pageId: next.pageId });
  }

  function recordProblem(problemId: string) {
    problemActivity.current.activate(problemId);
    collector.current.record({ sessionId, type: 'PROBLEM_FIRST_INTERACTION', atMs: performance.now(), pageId: page.pageId, problemId });
  }

  function updateFirstPassAnswer(problem: MathWorksheetProblemV2, rawValue: string) {
    recordProblem(problem.problemId);
    const now = new Date().toISOString();
    setAnswersByProblemId((current) => ({
      ...current,
      [problem.problemId]: updateMathAnswerDraft(current[problem.problemId], rawValue, now),
    }));
  }

  function updateCorrectionAnswer(problem: MathWorksheetProblemV2, rawValue: string) {
    const id = questionId(problem);
    if (!id || !correctionTargets.has(id)) return;
    correctionActivity.current.activate(id);
    const now = new Date().toISOString();
    setCorrectionAnswersByQuestionId((current) => ({
      ...current,
      [id]: updateMathAnswerDraft(current[id], rawValue, now),
    }));
  }

  function firstPassTimings(atMs: number) {
    return Object.fromEntries(allProblems.map((problem) => [
      problem.problemId,
      problemActivity.current.snapshot(problem.problemId, atMs),
    ])) as Record<string, ProblemTimingSnapshotV2 | undefined>;
  }

  function correctionTimings(atMs: number) {
    return Object.fromEntries(activeCorrectionProblems.map((problem) => {
      const id = questionId(problem)!;
      return [id, correctionActivity.current.snapshot(id, atMs)];
    })) as Record<string, ProblemTimingSnapshotV2 | undefined>;
  }

  async function finishStudy() {
    if (!runtime || !runtimeReady || missingFirstPassAnswers.length > 0 || !interaction.allowBatchRequest || submissionLock.current) return;
    submissionLock.current = true;
    const clientBatchSubmissionId = evaluation.clientBatchSubmissionId ?? `le-v2-batch-${crypto.randomUUID()}`;
    const firstPassSnapshot = cloneInkMapV2(inkByPage);
    setFrozenFirstPassInkByPage(firstPassSnapshot);
    persistFirstPassCheckpoint(firstPassSnapshot);
    const requested = requestBatchSubmission(evaluation, clientBatchSubmissionId);
    setEvaluation(requested.state);
    recordTelemetry(requested.effect.telemetry);
    const completedAtMs = Date.now();
    const duration = sessionTimer.current.snapshot();
    const context: StudyBatchSubmissionContextV2 = {
      clientBatchSubmissionId,
      studySessionId: runtime.studySessionId,
      assignmentId: runtime.assignmentId,
      worksheetVersionId: runtime.worksheetVersionId,
      submittedAt: new Date(completedAtMs).toISOString(),
      telemetry: {
        elapsedDurationMs: duration.elapsedDurationMs,
        activeDurationMs: duration.activeDurationMs,
        pausedDurationMs: duration.pausedDurationMs,
        eventCursor: collector.current.events().at(-1)?.eventId ?? null,
      },
    };
    try {
      const request = buildStudyBatchSubmissionV2({
        worksheet,
        answersByProblemId,
        timingByProblemId: firstPassTimings(completedAtMs),
        context,
      });
      const dispatched = markBatchDispatched(requested.state);
      setEvaluation(dispatched.state);
      recordTelemetry(dispatched.effect.telemetry);
      const result = await submitStudyBatchV2(runtime.client, request);
      const resolved = resolveBatchSubmission(dispatched.state, result);
      setEvaluation(resolved.state);
      recordTelemetry(resolved.effect.telemetry);
      if (resolved.state.mode === 'correction' && resolved.state.remainingQuestionInstanceIds[0]) {
        setFrozenFirstPassInkByPage(firstPassSnapshot);
        setCorrectionAnswersByQuestionId({});
        setCorrectionInkByPage({});
        correctionActivity.current = new ProblemActivityTrackerV2();
        setPageIndex(pageIndexForQuestion(worksheet, resolved.state.remainingQuestionInstanceIds[0]));
      }
    } catch (error) {
      const rejected = rejectBatchSubmission(requested.state, error);
      setEvaluation(rejected.state);
    } finally {
      submissionLock.current = false;
    }
  }

  async function submitCorrections() {
    if (!runtime || !interaction.allowCorrectionRequest || missingCorrectionAnswers.length > 0 || submissionLock.current) return;
    submissionLock.current = true;
    const clientCorrectionSubmissionId = evaluation.activeCorrectionSubmissionId ?? `le-v2-correction-${crypto.randomUUID()}`;
    const requested = requestCorrectionSubmission(evaluation, clientCorrectionSubmissionId);
    setEvaluation(requested.state);
    const completedAtMs = Date.now();
    try {
      const request = buildStudyCorrectionSubmissionV2({
        worksheet,
        remainingQuestionInstanceIds: requested.state.remainingQuestionInstanceIds,
        correctionAnswersByQuestionInstanceId: correctionAnswersByQuestionId,
        timingByQuestionInstanceId: correctionTimings(completedAtMs),
        context: {
          clientCorrectionSubmissionId,
          clientBatchSubmissionId: requested.state.clientBatchSubmissionId!,
          studySessionId: runtime.studySessionId,
          assignmentId: runtime.assignmentId,
          submittedAt: new Date(completedAtMs).toISOString(),
        },
      });
      const dispatched = markCorrectionDispatched(requested.state);
      setEvaluation(dispatched.state);
      recordTelemetry(dispatched.effect.telemetry);
      const result = await submitStudyCorrectionV2(runtime.client, request);
      const resolved = resolveCorrectionSubmission(dispatched.state, result);
      setEvaluation(resolved.state);
      recordTelemetry(resolved.effect.telemetry);
      if (resolved.state.mode === 'correction' && resolved.state.remainingQuestionInstanceIds[0]) {
        setCorrectionAnswersByQuestionId({});
        correctionActivity.current = new ProblemActivityTrackerV2();
        setPageIndex(pageIndexForQuestion(worksheet, resolved.state.remainingQuestionInstanceIds[0]));
      }
    } catch (error) {
      const rejected = rejectCorrectionSubmission(requested.state, error);
      setEvaluation(rejected.state);
    } finally {
      submissionLock.current = false;
    }
  }

  const correctionMode = evaluation.mode === 'correction' || evaluation.mode === 'correction_submitting';
  return (
    <main className="v2-study-mode">
      <header className="v2-study-toolbar">
        <b>BelajarKu · Matematika</b>
        <span>{correctionMode ? `Perbaikan ${Math.max(1, evaluation.correctionRound + 1)}` : 'Kerjakan sampai selesai'}</span>
      </header>

      {interaction.firstPassEditable && (
        <section className="v2-study-guidance" aria-label="Cara mengerjakan">
          <strong>Kerjakan satu per satu. Bantuan akan berkurang sedikit demi sedikit.</strong>
          <span>Kerjakan berurutan seperti lembar latihan. Gunakan area kerja untuk coretan. Area kerja awal diberi panduan ringan, lalu dibuat semakin sederhana; isi hasil akhir tetap pada kotak jawaban.</span>
        </section>
      )}

      {correctionMode && (
        <section className="v2-correction-toolbar" aria-live="polite">
          <strong>Perbaiki soal yang ditandai ×</strong>
          <span>Jawaban pertama tetap tersimpan. Masukkan jawaban baru hanya untuk {evaluation.remainingQuestionInstanceIds.length} soal yang belum tepat.</span>
        </section>
      )}

      <section className="v2-paper" data-surface-layout={worksheet.surface.layout}>
        <header className="v2-paper-header">
          <div>
            <small>BelajarKu Mathematics</small>
            <strong>Worksheet Hari Ini</strong>
          </div>
          <div className="v2-paper-header-meta">
            <span>Halaman {page.pageNumber}/{worksheet.pages.length}</span>
            <span>{page.problems.length} soal</span>
            <span>Periksa setelah selesai</span>
          </div>
        </header>
        <div className="v2-paper-progress" aria-label="Urutan worksheet">
          {allProblems.map((problem) => <span key={problem.problemId} className={`v2-paper-progress-step v2-paper-progress-step--${problem.sequenceMeta.phase.toLowerCase()}${page.problems.some((candidate) => candidate.problemId === problem.problemId) ? ' is-current-page' : ''}`} />)}
        </div>
        <div className="v2-worksheet-content-layer">
          {page.problems.map((problem) => {
            const id = questionId(problem);
            const correctionRequired = Boolean(id && correctionTargets.has(id));
            const answerMode = correctionMode
              ? (correctionRequired && interaction.correctionEditable ? 'correction' : 'locked')
              : interaction.firstPassEditable ? 'study' : 'locked';
            const answerValue = correctionRequired && id
              ? correctionAnswersByQuestionId[id]?.rawValue ?? ''
              : answersByProblemId[problem.problemId]?.rawValue ?? '';
            return (
              <MathWorksheetProblemBlock
                key={problem.problemId}
                problem={problem}
                answerValue={answerValue}
                answerMode={answerMode}
                correctionRequired={correctionRequired}
                onAnswerInteraction={() => correctionRequired && id ? correctionActivity.current.activate(id) : recordProblem(problem.problemId)}
                onAnswerChange={answerMode === 'locked' ? undefined : (value) => correctionRequired ? updateCorrectionAnswer(problem, value) : updateFirstPassAnswer(problem, value)}
              />
            );
          })}
        </div>
        <WorksheetInkCanvas
          document={correctionMode ? currentCorrectionInk : currentInk}
          backgroundDocument={correctionMode ? currentFrozenFirstPassInk : undefined}
          onChange={(ink) => correctionMode
            ? setCorrectionInkByPage((current) => ({ ...current, [page.pageId]: ink }))
            : setInkByPage((current) => ({ ...current, [page.pageId]: ink }))}
          resolveProblemId={(x, y) => resolveProblemAtPoint(page, x, y)}
          canStartStroke={(problemId) => {
            if (!correctionMode) return interaction.firstPassEditable && Boolean(problemId);
            if (!interaction.correctionEditable) return false;
            const problem = page.problems.find((candidate) => candidate.problemId === problemId);
            const id = problem ? questionId(problem) : undefined;
            return Boolean(id && correctionTargets.has(id));
          }}
          onProblemFirstInteraction={(problemId) => {
            if (!correctionMode) {
              recordProblem(problemId);
              return;
            }
            const problem = page.problems.find((candidate) => candidate.problemId === problemId);
            const id = problem ? questionId(problem) : undefined;
            if (id && correctionTargets.has(id)) correctionActivity.current.activate(id);
          }}
          onSemanticEvent={(event) => {
            if (event.type === 'stroke_committed') {
              collector.current.record({ sessionId, type: 'STROKE_COMMITTED', atMs: performance.now(), pageId: page.pageId, metadata: { pointCount: event.pointCount } });
            }
            if (event.type === 'undo_used') collector.current.record({ sessionId, type: 'UNDO_USED', atMs: performance.now(), pageId: page.pageId });
            if (event.type === 'erase_used') collector.current.record({ sessionId, type: 'ERASE_USED', atMs: performance.now(), pageId: page.pageId });
          }}
        />
        <WorksheetMarkupLayer document={markup} />
      </section>

      {evaluation.lastError && <div className="v2-study-status" role="alert">Belum dapat diproses: {evaluation.lastError}</div>}
      {!runtimeReady && (
        <div className="v2-study-status">Isi semua jawaban akhir sebelum menyelesaikan latihan.</div>
      )}
      {evaluation.mode === 'await_review' && <div className="v2-study-status"><strong>Menunggu review tutor.</strong> Worksheet sudah terkirim dan dikunci sampai review authoritative selesai.</div>}
      {evaluation.mode === 'completed' && <div className="v2-study-status"><strong>Selesai.</strong> Batch dan seluruh koreksi sudah difinalisasi oleh evaluator.</div>}

      <footer className="v2-study-footer">
        <button type="button" disabled={pageIndex === 0} onClick={() => changePage(pageIndex - 1)}>Sebelumnya</button>
        <span>{pageIndex + 1} / {worksheet.pages.length}</span>
        <button type="button" disabled={pageIndex === worksheet.pages.length - 1} onClick={() => changePage(pageIndex + 1)}>Berikutnya</button>
        {interaction.showFirstPassAction && (
          <button className="v2-study-action" type="button" disabled={!runtimeReady || missingFirstPassAnswers.length > 0 || !interaction.allowBatchRequest} onClick={() => void finishStudy()}>
            {evaluation.mode === 'batch_submitting' ? 'Mengirim…' : missingFirstPassAnswers.length ? `Lengkapi ${missingFirstPassAnswers.length} jawaban` : 'Selesai Belajar'}
          </button>
        )}
        {interaction.showCorrectionAction && (
          <button className="v2-study-action" type="button" disabled={missingCorrectionAnswers.length > 0 || !interaction.allowCorrectionRequest} onClick={() => void submitCorrections()}>
            {evaluation.mode === 'correction_submitting' ? 'Memeriksa…' : missingCorrectionAnswers.length ? `Lengkapi ${missingCorrectionAnswers.length} koreksi` : 'Kirim Perbaikan'}
          </button>
        )}
      </footer>
    </main>
  );
}
